import { CentralizedUrlAdapter } from '../adapters/CentralizedUrlAdapter';
import {
  BillingFormSubmissionActionsController,
  PaymentMethodsController,
  BillingInfoFormController,
  UpdateBillingController,
} from '../controllers';
import { initializerHelpers } from '../helpers';
import type { Listeners } from '../helpers';
import {
  PaymentMethodsManager,
  BillingInfoFormManager,
  UpdateBillingManager,
  RouterNonUiManager,
  OverdueSummaryManager,
  AlertManager,
  EmailPlansManager,
} from '../managers';

export class BillingInfoInitializer {
  public listeners: Listeners;

  // managers
  private billingInfoForm: BillingInfoFormManager;

  private paymentMethods: PaymentMethodsManager;

  private updateBilling: UpdateBillingManager;

  private overdueSummary: OverdueSummaryManager;

  private alert: AlertManager;

  private routerManager: RouterNonUiManager;

  private emailPlansManager: EmailPlansManager;

  // init methods
  public billingInfoFormInit: BillingInfoFormManager['billingInfoFormInit'];

  public paymentMethodsInit: PaymentMethodsManager['paymentMethodsInit'];

  public updateBillingInit: UpdateBillingManager['updateBillingInit'];

  public routerNonUiInit: RouterNonUiManager['routerNonUiInit'];

  public overdueSummaryInit: OverdueSummaryManager['overdueSummaryInit'];

  public alertInit: AlertManager['alertInit'];

  public emailPlansInit: EmailPlansManager['emailPlansInit'];

  constructor() {
    const urlAdapter = new CentralizedUrlAdapter();

    this.billingInfoForm = new BillingInfoFormManager();
    this.paymentMethods = new PaymentMethodsManager(urlAdapter);
    this.updateBilling = new UpdateBillingManager();
    this.overdueSummary = new OverdueSummaryManager();
    this.alert = new AlertManager();
    this.routerManager = new RouterNonUiManager();
    this.emailPlansManager = new EmailPlansManager(urlAdapter);

    this.billingInfoFormInit = this.billingInfoForm.billingInfoFormInit;
    this.paymentMethodsInit = this.paymentMethods.paymentMethodsInit;
    this.updateBillingInit = this.updateBilling.updateBillingInit;
    this.routerNonUiInit = this.routerManager.routerNonUiInit;
    this.overdueSummaryInit = this.overdueSummary.overdueSummaryInit;
    this.alertInit = this.alert.alertInit;
    this.emailPlansInit = this.emailPlansManager.emailPlansInit;

    this.redirectIfHasOverdueBalance(this.overdueSummary, this.routerManager);

    this.listeners = this.initializeControllers();
  }

  private initializeControllers() {
    const controllers = [
      new BillingInfoFormController(this.billingInfoForm, this.paymentMethods),
      new PaymentMethodsController(this.paymentMethods, this.alert, this.updateBilling),
      new UpdateBillingController(this.paymentMethods, this.billingInfoForm, this.updateBilling, this.alert),
      new BillingFormSubmissionActionsController(this.updateBilling),
    ];

    initializerHelpers.initControllers(controllers);

    return initializerHelpers.createListeners(controllers);
  }

  private async redirectIfHasOverdueBalance(
    overdueSummaryManager: OverdueSummaryManager,
    routerManager: RouterNonUiManager
  ) {
    const api = await overdueSummaryManager.getApiWhenReady();

    // if has overdue balance, redirect to overdue balance page
    if (api.hasOverdueBalance()) {
      routerManager
        .getApiWhenReady()
        .then((routerApi) => routerApi.goToOverdueBalance({ replace: true, keepParams: true }));
    }
  }
}
